/**
 * UserRolesWorkflowScreen — User Role management
 * Layout: ERPAdvancedGrid (left) + Detail panel (right) + context menu
 */
import React, { useState, useEffect, useRef } from 'react';
import { API_BASE, getToken } from '../../lib/api';
import { Shield, Save, Trash2, X, Edit2, Plus, Tag } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import { erpToast } from '../../components/erp/ERPToast';

function authFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(opts.headers || {}),
    },
  });
}

const ROLE_COLS = [
  { field: 'URoleId',          header: 'Role ID',     frozen: true  },
  { field: 'UGroupName',       header: 'Group',       frozen: false },
  { field: 'URoleName',        header: 'Role Name',   frozen: false },
  { field: 'URoleDescription', header: 'Description', frozen: false },
  { field: 'UActiveLabel',     header: 'Active',      frozen: false },
];

const EMPTY_FORM = { UGroupId: '', URoleName: '', URoleDescription: '', UActive: true };

export default function UserRolesWorkflowScreen({ theme, onBack }) {
  const isDark = theme === 'dark';

  const [roles,       setRoles]       = useState([]);
  const [groups,      setGroups]      = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isNew,       setIsNew]       = useState(false);
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(20);
  const [sortField,   setSortField]   = useState('URoleId');
  const [sortDir,     setSortDir]     = useState('asc');
  const [filters,     setFilters]     = useState({});
  const [ctxMenu,     setCtxMenu]     = useState(null);
  const ctxRef = useRef(null);

  const panel = isDark ? '#161b27' : '#ffffff';
  const bd    = isDark ? '#1e2533' : '#e2e8f0';
  const tx    = isDark ? '#e2e8f0' : '#1e293b';
  const mt    = isDark ? '#64748b' : '#94a3b8';
  const ac    = '#6366f1';

  // ── load helpers (before useEffect) ──────────────────────────────────────────
  // refreshRoles is called from handleSave/handleDelete (click handlers only)
  const refreshRoles = async () => {
    setLoading(true);
    try {
      const d = await authFetch('/api/uams/roles').then(r => r.json());
      if (d.success) {
        setRoles((d.data || []).map(r => ({ ...r, UActiveLabel: r.UActive ? 'Yes' : 'No' })));
      }
    } catch (_) { erpToast.error('Failed to reload roles'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!formData.URoleName.trim()) { erpToast.error('Role Name is required'); return; }
    if (!formData.UGroupId) { erpToast.error('Group is required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const d = await authFetch('/api/uams/roles', {
          method: 'POST',
          body: JSON.stringify({ UGroupId: formData.UGroupId, URoleName: formData.URoleName, URoleDescription: formData.URoleDescription }),
        }).then(r => r.json());
        if (d.success) {
          erpToast.success(`Role "${formData.URoleName}" created`);
          setIsNew(false);
          setFormData(EMPTY_FORM);
          await refreshRoles();
        } else { erpToast.error(d.error || 'Create failed'); }
      } else {
        const d = await authFetch(`/api/uams/roles/${selectedRow.URoleId}`, {
          method: 'PUT',
          body: JSON.stringify({ UGroupId: formData.UGroupId, URoleName: formData.URoleName, URoleDescription: formData.URoleDescription, UActive: formData.UActive }),
        }).then(r => r.json());
        if (d.success) {
          erpToast.success('Role saved');
          await refreshRoles();
        } else { erpToast.error(d.error || 'Save failed'); }
      }
    } catch (_) { erpToast.error('Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.URoleName}"?\nThis action cannot be undone.`)) return;
    try {
      const d = await authFetch(`/api/uams/roles/${role.URoleId}`, { method: 'DELETE' }).then(r => r.json());
      if (d.success) {
        erpToast.success('Role deleted');
        if (selectedRow?.URoleId === role.URoleId) { setSelectedRow(null); setFormData(EMPTY_FORM); }
        await refreshRoles();
      } else { erpToast.error(d.error || 'Delete failed'); }
    } catch (_) { erpToast.error('Network error'); }
  };

  // ── useEffect — initial load only, inline async per project ESLint pattern ────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [rRes, gRes] = await Promise.all([
          authFetch('/api/uams/roles').then(r => r.json()),
          authFetch('/api/uams/groups').then(r => r.json()),
        ]);
        if (rRes.success) setRoles((rRes.data || []).map(r => ({ ...r, UActiveLabel: r.UActive ? 'Yes' : 'No' })));
        if (gRes.success) setGroups(gRes.data || []);
      } catch (_) { erpToast.error('Failed to load data'); }
      finally { setLoading(false); }
    };
    init();
  }, []);

  // Outside-click dismisses context menu — stable effect with empty deps (ctxRef guards null)
  useEffect(() => {
    const handler = (e) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target)) setCtxMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── helpers ───────────────────────────────────────────────────────────────────
  const openEdit = (row) => {
    setIsNew(false);
    setSelectedRow(row);
    setFormData({ UGroupId: row.UGroupId || '', URoleName: row.URoleName, URoleDescription: row.URoleDescription || '', UActive: row.UActive !== false });
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    const tr = e.target.closest('tr[data-testid]');
    if (!tr) return;
    const testId = tr.getAttribute('data-testid');
    const parts = testId.split('-row-');
    if (parts.length < 2) return;
    const rowId = parts[1];
    const row = roles.find(r => r.URoleId === rowId);
    if (row) setCtxMenu({ x: e.clientX, y: e.clientY, row });
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  // ── derived data ──────────────────────────────────────────────────────────────
  const filtered = roles.filter(r =>
    Object.entries(filters).every(([k, v]) => !v || String(r[k] || '').toLowerCase().includes(v.toLowerCase()))
  );
  const sorted = [...filtered].sort((a, b) => {
    const cmp = String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);
  const panelOpen = isNew || !!selectedRow;

  return (
    <div
      data-testid="user-roles-screen"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      onContextMenu={handleContextMenu}
    >
      {/* Context Menu */}
      {ctxMenu && (
        <div
          ref={ctxRef}
          data-testid="roles-ctx-menu"
          style={{
            position: 'fixed', top: ctxMenu.y, left: ctxMenu.x,
            zIndex: 9999, background: panel, border: `1px solid ${bd}`,
            borderRadius: 6, boxShadow: '0 4px 20px rgba(0,0,0,.18)',
            minWidth: 160, overflow: 'hidden',
          }}
        >
          <div
            data-testid="ctx-edit-role"
            onClick={() => { openEdit(ctxMenu.row); setCtxMenu(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: tx }}
            onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = isDark ? '#1e2533' : '#f1f5f9'; }}
            onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'transparent'; }}
          >
            <Edit2 size={12} color={ac} /> Edit Role
          </div>
          <div style={{ borderTop: `1px solid ${bd}`, margin: '2px 0' }} />
          <div
            data-testid="ctx-delete-role"
            onClick={() => { handleDelete(ctxMenu.row); setCtxMenu(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: '#ef4444' }}
            onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = '#fee2e2'; }}
            onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'transparent'; }}
          >
            <Trash2 size={12} /> Delete Role
          </div>
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Left: Grid */}
        <div style={{ flex: 1, overflow: 'hidden', minHeight: 0, minWidth: 0 }}>
          <ERPAdvancedGrid
            title="User Roles"
            columns={ROLE_COLS}
            data={paginated}
            total={filtered.length}
            idField="URoleId"
            nameField="URoleName"
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={s => { setPageSize(s); setPage(1); }}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            filters={filters}
            onFilterChange={f => { setFilters(f); setPage(1); }}
            loading={loading}
            onAddNew={() => { setIsNew(true); setSelectedRow(null); setFormData(EMPTY_FORM); }}
            onEdit={openEdit}
            onVoid={handleDelete}
            testId="user-roles-grid"
            selectedRowId={selectedRow?.URoleId}
            onRowClick={openEdit}
            onBack={onBack}
          />
        </div>

        {/* Right: Detail Panel */}
        {panelOpen && (
          <div
            data-testid="roles-detail-panel"
            style={{ width: 360, flexShrink: 0, borderLeft: `1px solid ${bd}`, background: panel, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: `1px solid ${bd}`, background: isDark ? '#1a2035' : '#f8fafc', flexShrink: 0 }}>
              <Tag size={14} color={ac} />
              <span style={{ fontWeight: 700, fontSize: 12, color: tx, marginLeft: 6, flex: 1 }}>
                {isNew ? 'New Role' : selectedRow?.URoleName}
              </span>
              <button
                data-testid="close-role-panel"
                onClick={() => { setSelectedRow(null); setIsNew(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mt, padding: 2 }}
              >
                <X size={14} />
              </button>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {!isNew && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>ROLE ID</div>
                    <div style={{ padding: '5px 8px', background: isDark ? '#1e2533' : '#f8fafc', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, color: mt }}>{selectedRow?.URoleId}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>GROUP *</div>
                  <select
                    data-testid="role-group-select"
                    value={formData.UGroupId}
                    onChange={e => setFormData(p => ({ ...p, UGroupId: e.target.value }))}
                    style={{ width: '100%', padding: '6px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx }}
                  >
                    <option value="">-- Select Group --</option>
                    {groups.filter(g => g.UActive !== false).map(g => (
                      <option key={g.UGroupId} value={g.UGroupId}>{g.UGroupName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>ROLE NAME *</div>
                  <input
                    data-testid="role-name-input"
                    value={formData.URoleName}
                    onChange={e => setFormData(p => ({ ...p, URoleName: e.target.value }))}
                    placeholder="Enter role name"
                    style={{ width: '100%', padding: '6px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>DESCRIPTION</div>
                  <textarea
                    data-testid="role-desc-input"
                    value={formData.URoleDescription}
                    onChange={e => setFormData(p => ({ ...p, URoleDescription: e.target.value }))}
                    placeholder="Role description"
                    rows={3}
                    style={{ width: '100%', padding: '6px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
                {!isNew && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>ACTIVE</div>
                    <div style={{ display: 'flex', gap: 16 }}>
                      {[true, false].map(v => (
                        <label key={String(v)} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12 }}>
                          <input type="radio" name="uactive-role" checked={formData.UActive === v} onChange={() => setFormData(p => ({ ...p, UActive: v }))} style={{ accentColor: ac }} />
                          <span style={{ color: tx }}>{v ? 'Yes' : 'No'}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    data-testid="save-role-btn"
                    onClick={handleSave}
                    disabled={saving}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 0', background: saving ? '#a5b4fc' : ac, color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}
                  >
                    <Save size={13} /> {saving ? 'Saving...' : (isNew ? 'Create Role' : 'Save Changes')}
                  </button>
                  <button
                    data-testid="cancel-role-btn"
                    onClick={() => { setSelectedRow(null); setIsNew(false); }}
                    style={{ padding: '7px 12px', background: 'transparent', border: `1px solid ${bd}`, borderRadius: 5, color: mt, fontSize: 12, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
                {!isNew && selectedRow && (
                  <button
                    data-testid="delete-role-btn"
                    onClick={() => handleDelete(selectedRow)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 0', background: 'transparent', border: `1px solid #fca5a5`, borderRadius: 5, color: '#ef4444', fontSize: 11, cursor: 'pointer', marginTop: 4 }}
                  >
                    <Trash2 size={12} /> Delete Role
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {!panelOpen && !loading && (
          <div style={{ width: 200, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: mt, fontSize: 11, fontStyle: 'italic', borderLeft: `1px solid ${bd}`, background: isDark ? '#0f1117' : '#f8fafc' }}>
            <div style={{ textAlign: 'center', padding: 12 }}>
              <Shield size={20} color={bd} />
              <div style={{ marginTop: 6 }}>Click a row to edit or use Add New</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
